import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  Download,
  RefreshCw,
  AlignStartVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignEndHorizontal,
  ArrowLeft,
  Upload,
  Check,
  Loader2,
  Save,
  CheckCircle2,
  Droplets,
  Grid3X3,
  Square,
  CornerLeftUp,
  CornerRightUp,
  CornerLeftDown,
  CornerRightDown,
  AlertTriangle,
  Crown,
} from 'lucide-react';
import { StepIndicator } from '@/components/StepIndicator';
import { BrandingConfig, GenerationResult, LogoPosition, WatermarkPosition, BrandingAsset, AspectRatio, Project, GalleryImage, HeroOverlayConfig } from '@/types';
import { fetchBrandingAssets } from '@/services/brandingService';
import { fetchProjectGalleryImages } from '@/services/projectService';
import { HeroOverlayEditor } from '@/components/ui';
import { AiLabelOption, type AiLabelPrefs } from '@/components/ui/AiLabelOption';
import { useAuth } from '@/context/AuthContext';
import { processWatermark, WatermarkOptions } from '@/services/imageProcessing';
import { supabase } from '@/lib/supabase';
import { overlaysForSelection } from '@/lib/overlayVariants';

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface BrandingViewProps {
  branding: BrandingConfig;
  results: GenerationResult[];
  isProcessing: boolean;
  onBrandingUpload: (e: React.ChangeEvent<HTMLInputElement>, field: 'logo' | 'overlay') => void;
  onBrandingUpdate: (updates: Partial<BrandingConfig>) => void;
  onDownloadAll: (compressed: boolean) => void;
  /** KI-Hinweis-Voreinstellung — im App-State gehalten, weil der ZIP-Handler dort lebt. */
  aiLabel?: AiLabelPrefs;
  onBack?: () => void;
  onSaveAndClose?: () => Promise<void>; // Save branded images and return to project details
  projectAspectRatio?: AspectRatio; // Project's aspect ratio for validation
  defaultAssetIds?: {
    logoAssetId: string | null;
    overlayAssetId: string | null;
    watermarkAssetId: string | null;
  } | null;
  project?: Project;
  onHeroSave?: (imageId: string, heroDataUrl: string, config: HeroOverlayConfig) => Promise<string>;
  stepIndicator?: React.ReactNode;
}

// Tolerance for aspect ratio comparison (+/- 0.05)
const ASPECT_RATIO_TOLERANCE = 0.05;

// Expected ratios
const EXPECTED_RATIOS: Record<AspectRatio, number> = {
  '1:1': 1.0,
  '4:3': 4 / 3, // ~1.333
};

/**
 * Validate if an overlay's aspect ratio matches the expected project ratio
 */
const validateOverlayRatio = (
  overlayWidth: number,
  overlayHeight: number,
  projectRatio: AspectRatio
): { isValid: boolean; overlayRatio: number; expectedRatio: number } => {
  const overlayRatio = overlayWidth / overlayHeight;
  const expectedRatio = EXPECTED_RATIOS[projectRatio];
  const isValid = Math.abs(overlayRatio - expectedRatio) <= ASPECT_RATIO_TOLERANCE;
  
  return { isValid, overlayRatio, expectedRatio };
};

export const BrandingView: React.FC<BrandingViewProps> = ({
  branding,
  results,
  isProcessing,
  onBrandingUpload,
  onBrandingUpdate,
  onDownloadAll,
  aiLabel,
  onBack,
  onSaveAndClose,
  projectAspectRatio = '4:3',
  defaultAssetIds,
  project,
  onHeroSave,
  stepIndicator,
}) => {
  const { user } = useAuth();
  const previewResult = results.find((r) => r.currentUrl);

  // Asset library state
  const [logos, setLogos] = useState<BrandingAsset[]>([]);
  const [overlays, setOverlays] = useState<BrandingAsset[]>([]);
  const [watermarks, setWatermarks] = useState<BrandingAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Selection mode (now handled by Tabs)
  const [logoMode, setLogoMode] = useState<string>('library');
  const [overlayMode, setOverlayMode] = useState<string>('library');
  const [watermarkMode, setWatermarkMode] = useState<string>('library');

  // Selected asset IDs
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(null);

  // Save toast state
  const [showSaveToast, setShowSaveToast] = useState(false);

  // Saving state (separate from isProcessing which is for download)
  const [isSaving, setIsSaving] = useState(false);

  // Watermark preview canvas
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState<string | null>(null);

  // Overlay aspect ratio mismatch warning
  const [overlayMismatchWarning, setOverlayMismatchWarning] = useState<string | null>(null);

  // Hero Titelbild state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [heroEditorImage, setHeroEditorImage] = useState<GalleryImage | null>(null);

  // Load gallery images for hero selection
  useEffect(() => {
    if (!project) return;
    const loadGallery = async () => {
      try {
        const imgs = await fetchProjectGalleryImages(project.id);
        // Show all images that have any renderable URL
        setGalleryImages(imgs.filter(img => img.stagedUrl || img.emptyUrl || img.originalUrl));
      } catch (error) {
        console.error('[Branding] Failed to load gallery for hero:', error);
      }
    };
    loadGallery();
  }, [project]);

  // Load saved assets (check if user is team member -> load admin's assets)
  useEffect(() => {
    const loadAssets = async () => {
      if (!user) return;
      
      setIsLoadingAssets(true);
      try {
        // Check if user is a team member - if so, load admin's assets
        let assetOwnerId = user.id;
        
        const { data: adminSettingsData, error: adminSettingsError } = await supabase
          .rpc('get_admin_settings');
        
        if (!adminSettingsError && adminSettingsData && adminSettingsData.length > 0) {
          // User is a team member - use admin's ID for loading assets
          assetOwnerId = adminSettingsData[0].admin_id;
        }

        const [logoData, overlayData, watermarkData] = await Promise.all([
          fetchBrandingAssets(assetOwnerId, 'logo'),
          fetchBrandingAssets(assetOwnerId, 'overlay'),
          fetchBrandingAssets(assetOwnerId, 'watermark'),
        ]);
        setLogos(logoData);
        setOverlays(overlayData);
        setWatermarks(watermarkData);

        // Auto-select default assets if set and no asset is already selected
        if (defaultAssetIds) {
          if (defaultAssetIds.logoAssetId && !branding.logoUrl) {
            const asset = logoData.find(a => a.id === defaultAssetIds.logoAssetId);
            if (asset) {
              setSelectedLogoId(asset.id);
              onBrandingUpdate({ logo: null, logoUrl: asset.publicUrl });
            }
          }
          if (defaultAssetIds.overlayAssetId && !branding.overlayUrl) {
            const asset = overlayData.find(a => a.id === defaultAssetIds.overlayAssetId);
            if (asset) {
              setSelectedOverlayId(asset.id);
              onBrandingUpdate({ overlay: null, overlayUrl: asset.publicUrl });
            }
          }
          if (defaultAssetIds.watermarkAssetId && !branding.watermarkUrl) {
            const asset = watermarkData.find(a => a.id === defaultAssetIds.watermarkAssetId);
            if (asset) {
              setSelectedWatermarkId(asset.id);
              onBrandingUpdate({ watermarkUrl: asset.publicUrl });
            }
          }
        }
      } catch (error) {
        console.error('Error loading branding assets:', error);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    loadAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Generate watermark preview when watermark settings change
  useEffect(() => {
    const generateWatermarkPreview = async () => {
      if (!branding.watermarkUrl) {
        setWatermarkPreviewUrl(null);
        return;
      }

      try {
        const previewSize = 200;
        const options: WatermarkOptions = {
          opacity: branding.watermarkOpacity,
          position: branding.watermarkPosition,
          scale: branding.watermarkScale,
        };
        
        const processedCanvas = await processWatermark(
          branding.watermarkUrl,
          previewSize,
          previewSize,
          options
        );
        
        setWatermarkPreviewUrl(processedCanvas.toDataURL());
      } catch (error) {
        console.error('Error generating watermark preview:', error);
      }
    };

    generateWatermarkPreview();
  }, [branding.watermarkUrl, branding.watermarkOpacity, branding.watermarkPosition, branding.watermarkScale]);

  // Validate overlay aspect ratio when overlay changes
  useEffect(() => {
    if (!branding.overlayUrl) {
      setOverlayMismatchWarning(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const { isValid, overlayRatio } = validateOverlayRatio(
        img.width,
        img.height,
        projectAspectRatio
      );

      if (!isValid) {
        const overlayFormat = overlayRatio > 1.2 ? 'Querformat' : overlayRatio < 0.9 ? 'Hochformat' : 'Quadratisch';
        const expectedFormat = projectAspectRatio === '4:3' ? '4:3 (Querformat)' : '1:1 (Quadratisch)';
        setOverlayMismatchWarning(
          `Das Overlay-Format (${overlayFormat}, ${overlayRatio.toFixed(2)}) passt nicht zum Bildformat (${expectedFormat}). Es wird gestreckt/gestaucht dargestellt.`
        );
      } else {
        setOverlayMismatchWarning(null);
      }
    };
    img.onerror = () => {
      console.error('Failed to load overlay image for validation');
    };
    img.src = branding.overlayUrl;
  }, [branding.overlayUrl, projectAspectRatio]);

  // Handle selecting a logo from library
  const handleSelectLogo = useCallback((asset: BrandingAsset) => {
    setSelectedLogoId(asset.id);
    onBrandingUpdate({ 
      logo: null, // Clear file since we're using URL
      logoUrl: asset.publicUrl 
    });
  }, [onBrandingUpdate]);

  // Handle selecting an overlay from library
  const handleSelectOverlay = useCallback((asset: BrandingAsset) => {
    setSelectedOverlayId(asset.id);
    onBrandingUpdate({ 
      overlay: null, // Clear file since we're using URL
      overlayUrl: asset.publicUrl 
    });
  }, [onBrandingUpdate]);

  // Handle selecting a watermark from library
  const handleSelectWatermark = useCallback((asset: BrandingAsset) => {
    setSelectedWatermarkId(asset.id);
    onBrandingUpdate({ watermarkUrl: asset.publicUrl });
  }, [onBrandingUpdate]);

  // Handle close with actual save
  const handleCloseAndSave = useCallback(async () => {
    if (!onSaveAndClose) return;
    
    setIsSaving(true);
    try {
      await onSaveAndClose();
      // Show success toast briefly before navigation happens
      setShowSaveToast(true);
    } catch (error) {
      console.error('Save failed:', error);
      // Error is already handled in onSaveAndClose
    } finally {
      setIsSaving(false);
    }
  }, [onSaveAndClose]);

  const positionButtons: { position: LogoPosition; icon: React.ReactNode }[] = [
    { position: 'top-left', icon: <AlignStartVertical className="w-4 h-4" /> },
    { position: 'top-right', icon: <AlignEndVertical className="w-4 h-4" /> },
    { position: 'bottom-left', icon: <AlignStartHorizontal className="w-4 h-4" /> },
    { position: 'bottom-right', icon: <AlignEndHorizontal className="w-4 h-4" /> },
  ];

  const watermarkPositionButtons: { position: WatermarkPosition; icon: React.ReactNode; label: string }[] = [
    { position: 'center', icon: <Square className="w-4 h-4" />, label: 'Mitte' },
    { position: 'tile', icon: <Grid3X3 className="w-4 h-4" />, label: 'Gekachelt' },
    { position: 'tl', icon: <CornerLeftUp className="w-4 h-4" />, label: 'OL' },
    { position: 'tr', icon: <CornerRightUp className="w-4 h-4" />, label: 'OR' },
    { position: 'bl', icon: <CornerLeftDown className="w-4 h-4" />, label: 'UL' },
    { position: 'br', icon: <CornerRightDown className="w-4 h-4" />, label: 'UR' },
  ];

  // Count results with both staged and empty versions
  const stagedCount = results.filter(r => r.type === 'Staged' && r.currentUrl).length;
  const emptyCount = results.filter(r => r.type === 'Empty' && r.currentUrl).length;

  return (
    <div className="space-y-8 animate-fade-in relative">
      {/* Save Toast */}
      {showSaveToast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Gespeichert!</span>
          </div>
        </div>
      )}

      {stepIndicator || <StepIndicator currentStep="branding" onBack={onBack} />}

      {/* Back Button */}
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="-mt-4 gap-2">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Überprüfung
        </Button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Controls */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                 <ShieldCheck className="w-5 h-5 text-primary" /> Branding Assets
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">
                    Agentur-Logo (Wasserzeichen)
                  </Label>
                  {branding.logoUrl && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        onBrandingUpdate({ logo: null, logoUrl: null });
                        setSelectedLogoId(null);
                      }}
                      className="text-destructive h-auto p-0"
                    >
                      Entfernen
                    </Button>
                  )}
                </div>

                <Tabs value={logoMode} onValueChange={setLogoMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="library">Bibliothek</TabsTrigger>
                    <TabsTrigger value="upload">Hochladen</TabsTrigger>
                  </TabsList>
                  <TabsContent value="library" className="mt-2">
                    <div className="border rounded-lg p-3 bg-muted/30">
                        {isLoadingAssets ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 text-primary animate-spin" />
                          </div>
                        ) : logos.length === 0 ? (
                          <div className="text-center py-4 text-muted-foreground text-xs">
                            Keine gespeicherten Logos.
                            <br />
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => setLogoMode('upload')}
                              className="mt-1 h-auto p-0"
                            >
                              Neues hochladen
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto pr-1">
                            {logos.map((asset) => (
                              <button
                                key={asset.id}
                                onClick={() => handleSelectLogo(asset)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                                  selectedLogoId === asset.id
                                    ? 'border-primary ring-2 ring-primary/20'
                                    : 'border-muted hover:border-primary/50'
                                }`}
                              >
                                <img
                                  src={asset.publicUrl}
                                  alt={asset.name}
                                  className="w-full h-full object-contain p-1 bg-white"
                                />
                                {selectedLogoId === asset.id && (
                                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-primary" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </TabsContent>
                  <TabsContent value="upload" className="mt-2">
                    <label className="flex-1 h-24 border-2 border-dashed border-muted rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-all">
                        {branding.logoUrl && !selectedLogoId ? (
                          <img src={branding.logoUrl} className="h-16 object-contain" alt="Logo" />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-muted-foreground">
                             <Upload className="w-5 h-5" />
                             <span className="text-xs">Logo hochladen</span>
                          </div>
                        )}
                        <Input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg"
                          onChange={(e) => {
                            onBrandingUpload(e, 'logo');
                            setSelectedLogoId(null);
                          }}
                        />
                      </label>
                  </TabsContent>
                </Tabs>

                {branding.logoUrl && (
                  <div className="mt-3">
                    <Label className="text-xs text-muted-foreground mb-2 block">Logo Position</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {positionButtons.map(({ position, icon }) => (
                        <Button
                          key={position}
                          variant="outline"
                          size="sm"
                          onClick={() => onBrandingUpdate({ logoPosition: position })}
                          className={`flex justify-center transition-colors ${
                            branding.logoPosition === position
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {icon}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Overlay Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">
                    Rahmen-Overlay (1:1 PNG)
                  </Label>
                  {branding.overlayUrl && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        onBrandingUpdate({ overlay: null, overlayUrl: null });
                        setSelectedOverlayId(null);
                      }}
                      className="text-destructive h-auto p-0"
                    >
                      Entfernen
                    </Button>
                  )}
                </div>

                 <Tabs value={overlayMode} onValueChange={setOverlayMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="library">Bibliothek</TabsTrigger>
                    <TabsTrigger value="upload">Hochladen</TabsTrigger>
                  </TabsList>
                  <TabsContent value="library" className="mt-2">
                    <div className="border rounded-lg p-3 bg-muted/30">
                        {isLoadingAssets ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 text-primary animate-spin" />
                          </div>
                        ) : overlaysForSelection(overlays).length === 0 ? (
                          <div className="text-center py-4 text-muted-foreground text-xs">
                            Keine gespeicherten Overlays.
                            <br />
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => setOverlayMode('upload')}
                              className="mt-1 h-auto p-0"
                            >
                              Neues hochladen
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto pr-1">
                            {overlaysForSelection(overlays).map((asset) => (
                              <button
                                key={asset.id}
                                onClick={() => handleSelectOverlay(asset)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors bg-muted/20 ${
                                  selectedOverlayId === asset.id
                                    ? 'border-primary ring-2 ring-primary/20'
                                    : 'border-muted hover:border-primary/50'
                                }`}
                              >
                                <img
                                  src={asset.publicUrl}
                                  alt={asset.name}
                                  className="w-full h-full object-contain p-1"
                                />
                                {selectedOverlayId === asset.id && (
                                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-primary" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </TabsContent>
                  <TabsContent value="upload" className="mt-2">
                    <label className="flex-1 h-24 border-2 border-dashed border-muted rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/30 transition-all">
                        {branding.overlayUrl && !selectedOverlayId ? (
                           <img
                            src={branding.overlayUrl}
                            className="h-full w-full object-contain p-2"
                            alt="Overlay"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-muted-foreground">
                             <Upload className="w-5 h-5" />
                             <span className="text-xs">Rahmen hochladen</span>
                          </div>
                        )}
                        <Input
                          type="file"
                          className="hidden"
                          accept="image/png"
                          onChange={(e) => {
                            onBrandingUpload(e, 'overlay');
                            setSelectedOverlayId(null);
                          }}
                        />
                      </label>
                  </TabsContent>
                </Tabs>
                <p className="text-xs text-muted-foreground mt-1">
                  Sollte {projectAspectRatio === '4:3' ? '4:3 (Querformat)' : '1:1 (Quadratisch)'} sein.
                </p>

                {/* Overlay Mismatch Warning */}
                {overlayMismatchWarning && (
                  <Alert variant="destructive" className="mt-3 py-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      {overlayMismatchWarning}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Watermark Section */}
          <Card>
             <CardHeader>
              <CardTitle className="flex items-center gap-2">
                 <Droplets className="w-5 h-5 text-cyan-600" /> Wasserzeichen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Watermark Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">
                    Wasserzeichen (S/W Bild)
                  </Label>
                  {branding.watermarkUrl && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        onBrandingUpdate({ watermarkUrl: null });
                        setSelectedWatermarkId(null);
                      }}
                      className="text-destructive h-auto p-0"
                    >
                      Entfernen
                    </Button>
                  )}
                </div>

                <Tabs value={watermarkMode} onValueChange={setWatermarkMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="library">Bibliothek</TabsTrigger>
                    <TabsTrigger value="upload">Hochladen</TabsTrigger>
                  </TabsList>
                  <TabsContent value="library" className="mt-2">
                     <div className="border rounded-lg p-3 bg-muted/30">
                        {isLoadingAssets ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 text-cyan-600 animate-spin" />
                          </div>
                        ) : watermarks.length === 0 ? (
                          <div className="text-center py-4 text-muted-foreground text-xs">
                            Keine gespeicherten Wasserzeichen.
                            <br />
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => setWatermarkMode('upload')}
                              className="mt-1 h-auto p-0"
                            >
                              Neues hochladen
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto pr-1">
                            {watermarks.map((asset) => (
                              <button
                                key={asset.id}
                                onClick={() => handleSelectWatermark(asset)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors bg-muted/20 ${
                                  selectedWatermarkId === asset.id
                                    ? 'border-cyan-500 ring-2 ring-cyan-200'
                                    : 'border-muted hover:border-cyan-300'
                                }`}
                              >
                                <img
                                  src={asset.publicUrl}
                                  alt={asset.name}
                                  className="w-full h-full object-contain p-1"
                                />
                                {selectedWatermarkId === asset.id && (
                                  <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-cyan-700" />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </TabsContent>
                  <TabsContent value="upload" className="mt-2">
                     <label className="flex-1 h-24 border-2 border-dashed border-muted rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-cyan-400 hover:bg-muted/30 transition-all">
                        {branding.watermarkUrl && !selectedWatermarkId ? (
                           <img
                            src={branding.watermarkUrl}
                            className="h-full w-full object-contain p-2"
                            alt="Watermark"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-muted-foreground">
                             <Upload className="w-5 h-5" />
                             <span className="text-xs">S/W Bild hochladen</span>
                          </div>
                        )}
                        <Input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg"
                          onChange={(e) => {
                             if (e.target.files?.[0]) {
                              const file = e.target.files[0];
                              const url = URL.createObjectURL(file);
                              onBrandingUpdate({ watermarkUrl: url });
                              setSelectedWatermarkId(null);
                            }
                          }}
                        />
                      </label>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Watermark Controls (only show if watermark selected) */}
              {branding.watermarkUrl && (
                <>
                  <Separator />
                  {/* Opacity Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-xs text-muted-foreground">Deckkraft</Label>
                      <span className="text-xs font-mono">{branding.watermarkOpacity}%</span>
                    </div>
                    <Slider
                      min={5}
                      max={80}
                      step={5}
                      value={[branding.watermarkOpacity]}
                      onValueChange={([value]) => onBrandingUpdate({ watermarkOpacity: value })}
                    />
                  </div>

                  {/* Scale Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-xs text-muted-foreground">Größe</Label>
                      <span className="text-xs font-mono">{Math.round(branding.watermarkScale * 100)}%</span>
                    </div>
                    <Slider
                      min={10}
                      max={100}
                      step={5}
                      value={[branding.watermarkScale * 100]}
                      onValueChange={([value]) => onBrandingUpdate({ watermarkScale: value / 100 })}
                    />
                  </div>

                  {/* Position Grid */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Position</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {watermarkPositionButtons.map(({ position, icon, label }) => (
                        <Button
                          key={position}
                          variant="outline"
                          size="sm"
                          onClick={() => onBrandingUpdate({ watermarkPosition: position })}
                          className={`flex flex-col items-center gap-1 h-auto py-2 text-xs ${
                            branding.watermarkPosition === position
                              ? 'bg-cyan-50 border-cyan-400 text-cyan-700'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {icon}
                          <span className="text-[10px]">{label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Preview processed watermark */}
                  {watermarkPreviewUrl && (
                    <div className="pt-2 border-t">
                      <Label className="text-xs text-muted-foreground mb-2 block">Vorschau (verarbeitet)</Label>
                      <div className="h-16 bg-slate-900 rounded-lg flex items-center justify-center overflow-hidden">
                        <img
                          src={watermarkPreviewUrl}
                          alt="Watermark Preview"
                          className="h-12 w-12 object-contain"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Weiß wird transparent, Schwarz behält die Deckkraft.
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Hero Titelbild Section */}
          {project && onHeroSave && galleryImages.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Hero Titelbild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Wähle ein Bild, um ein Titelbild mit Immobiliendaten zu erstellen.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {galleryImages.map((img) => {
                    const thumbUrl = img.stagedUrl || img.emptyUrl || img.originalUrl;
                    if (!thumbUrl) return null;
                    return (
                      <button
                        key={img.id}
                        onClick={() => setHeroEditorImage(img)}
                        className="aspect-[4/3] rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-all relative group"
                      >
                        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                        {img.heroOverlayConfig && (
                          <div className="absolute top-1 right-1 bg-amber-500/90 rounded-full p-0.5">
                            <Crown className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hero Overlay Editor Modal */}
          {heroEditorImage && project && onHeroSave && (
            <HeroOverlayEditor
              image={heroEditorImage}
              project={project}
              onClose={() => setHeroEditorImage(null)}
              onSave={async (imageId, heroDataUrl, config) => {
                const heroUrl = await onHeroSave(imageId, heroDataUrl, config);
                // Update local gallery state with config + URL
                setGalleryImages(prev => prev.map(img =>
                  img.id === imageId ? { ...img, heroOverlayConfig: config, heroUrl: heroUrl || img.heroUrl } : img
                ));
              }}
            />
          )}

          {/* Export Section */}
          <Card className="bg-primary/5 border-primary/20">
             <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-2 text-primary">Bereit zum Export?</h3>
              <p className="text-sm text-primary/80 mb-2">
                Logo, Wasserzeichen und Overlay werden auf alle generierten Bilder angewendet.
              </p>
              <div className="text-xs text-primary mb-4">
                • {stagedCount} eingerichtete Bilder<br />
                • {emptyCount} leere Bilder
              </div>

              {/* Letzte Export-Option direkt über den Download-Knöpfen */}
              {aiLabel && <AiLabelOption {...aiLabel} className="mb-4 border-primary/20" />}

              <div className="space-y-3">
                <Button
                  onClick={() => onDownloadAll(true)}
                  disabled={isProcessing}
                  className="w-full gap-2"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Verarbeitung...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" /> Komprimiert herunterladen
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => onDownloadAll(false)}
                  disabled={isProcessing}
                  variant="outline"
                  className="w-full gap-2"
                >
                  <Download className="w-4 h-4" /> Original herunterladen
                </Button>

                {onSaveAndClose && (
                  <Button
                    onClick={handleCloseAndSave}
                    disabled={isProcessing || isSaving}
                    variant="secondary"
                    className="w-full gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" /> Schließen & Speichern
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>
                Live-Vorschau ({projectAspectRatio})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {previewResult?.currentUrl ? (
                <>
                  <div 
                    className={`relative max-w-lg mx-auto rounded-xl overflow-hidden shadow-lg border border-border ${
                      projectAspectRatio === '1:1' ? 'aspect-square' : 'aspect-[4/3]'
                    }`}
                  >
                    {/* Base Image */}
                    <img
                      src={previewResult.currentUrl}
                      className="absolute inset-0 w-full h-full object-cover z-0"
                      alt="Base"
                    />

                    {/* Watermark Layer (between base and overlay) */}
                    {watermarkPreviewUrl && branding.watermarkUrl && (
                      <div 
                        className="absolute inset-0 z-5 pointer-events-none flex items-center justify-center"
                        style={{
                          ...(branding.watermarkPosition === 'tile' && {
                            backgroundImage: `url(${watermarkPreviewUrl})`,
                            backgroundRepeat: 'repeat',
                            backgroundSize: `${branding.watermarkScale * 50}%`,
                          }),
                        }}
                      >
                        {branding.watermarkPosition !== 'tile' && (
                          <img
                            src={watermarkPreviewUrl}
                            alt="Watermark"
                            style={{
                              width: `${branding.watermarkScale * 50}%`,
                              position: 'absolute',
                              ...(branding.watermarkPosition === 'center' && { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
                              ...(branding.watermarkPosition === 'tl' && { top: '5%', left: '5%' }),
                              ...(branding.watermarkPosition === 'tr' && { top: '5%', right: '5%' }),
                              ...(branding.watermarkPosition === 'bl' && { bottom: '5%', left: '5%' }),
                              ...(branding.watermarkPosition === 'br' && { bottom: '5%', right: '5%' }),
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Overlay Layer */}
                    {branding.overlayUrl && (
                      <img
                        src={branding.overlayUrl}
                        className="absolute inset-0 w-full h-full z-10 pointer-events-none"
                        alt="Overlay"
                      />
                    )}

                    {/* Logo Layer */}
                    {branding.logoUrl && (
                      <div
                        className={`absolute w-[15%] z-20 pointer-events-none transition-all duration-300
                          ${branding.logoPosition === 'top-left' ? 'top-[3%] left-[3%]' : ''}
                          ${branding.logoPosition === 'top-right' ? 'top-[3%] right-[3%]' : ''}
                          ${branding.logoPosition === 'bottom-left' ? 'bottom-[3%] left-[3%]' : ''}
                          ${branding.logoPosition === 'bottom-right' ? 'bottom-[3%] right-[3%]' : ''}
                        `}
                      >
                        <img
                          src={branding.logoUrl}
                          className="w-full h-auto drop-shadow-lg"
                          alt="Logo"
                        />
                      </div>
                    )}

                    <div className="absolute top-4 left-4 bg-black/60 text-white text-xs px-2 py-1 rounded backdrop-blur-md z-30">
                      Vorschau: {previewResult.type}
                    </div>
                  </div>

                  <p className="text-center text-xs text-muted-foreground mt-4">
                    Logo, Wasserzeichen und Overlay werden beim Download/Speichern in die finalen PNGs eingebrannt.
                  </p>
                </>
              ) : (
                <div className="text-muted-foreground text-center py-20">
                  Noch keine Bilder generiert.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
